import { useMemo, useRef, useState } from "react";
import type { ActionItem, Language, Meeting, TodoItem } from "../../types";
import { createId } from "../../lib/id";
import { createMockMeetingSummary, smartExtract } from "../../lib/mock";
import {
  removeMeeting,
  saveMeeting,
  saveTodo,
  saveTodos,
} from "../../lib/storage";
import { useI18n } from "../../i18n";

function download(name: string, content: string, type: string): void {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  URL.revokeObjectURL(url);
}

function toMarkdown(meeting: Meeting): string {
  return `# ${meeting.title}\n\n${meeting.summary.executiveSummary}\n\n## Decisions\n${meeting.summary.decisions.map((x) => `- ${x}`).join("\n") || "- None"}\n\n## Action items\n${meeting.summary.actionItems.map((x) => `- [${x.done ? "x" : " "}] ${x.task} — ${x.owner || "Unassigned"}${x.dueDate ? ` (${x.dueDate})` : ""}`).join("\n") || "- None"}\n\n## Risks\n${meeting.summary.risks.map((x) => `- ${x}`).join("\n") || "- None"}\n`;
}

export function Meetings({
  meetings,
  initialMeetingId,
  startNew,
  onRefresh,
  onTodosChanged,
  onClearIntent,
}: {
  meetings: Meeting[];
  initialMeetingId: string | null;
  startNew: boolean;
  onRefresh: () => Promise<void>;
  onTodosChanged: () => Promise<void>;
  onClearIntent: () => void;
}): React.JSX.Element {
  const { language, t } = useI18n();
  const [view, setView] = useState<"list" | "new" | "detail">(
    startNew ? "new" : initialMeetingId ? "detail" : "list",
  );
  const [selectedId, setSelectedId] = useState<string | null>(initialMeetingId);
  const [query, setQuery] = useState("");
  const [title, setTitle] = useState("");
  const [transcript, setTranscript] = useState("");
  const [sourceName, setSourceName] = useState<string | null>(null);
  const [extractedItems, setExtractedItems] = useState<ActionItem[]>([]);
  const [editingSummary, setEditingSummary] = useState(false);
  const [summaryDraft, setSummaryDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{
    kind: "success" | "error" | "info";
    text: string;
  } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const selected =
    meetings.find((meeting) => meeting.id === selectedId) ?? null;
  const filtered = useMemo(
    () =>
      meetings.filter((meeting) =>
        `${meeting.title} ${meeting.summary.executiveSummary}`
          .toLowerCase()
          .includes(query.toLowerCase()),
      ),
    [meetings, query],
  );

  const openNew = (): void => {
    setView("new");
    setTitle("");
    setTranscript("");
    setSourceName(null);
    setExtractedItems([]);
    setNotice(null);
    onClearIntent();
  };
  const openDetail = (id: string): void => {
    setSelectedId(id);
    setView("detail");
    onClearIntent();
  };
  const handleFile = async (file: File | undefined): Promise<void> => {
    if (!file) return;
    if (file.size > 5_000_000) {
      setNotice({ kind: "error", text: "File exceeds 5 MB." });
      return;
    }
    setTranscript(await file.text());
    setSourceName(file.name);
    if (!title) setTitle(file.name.replace(/\.(txt|md|srt|vtt)$/i, ""));
  };
  const extract = (): void => {
    if (transcript.trim().length < 10) {
      setNotice({ kind: "error", text: t("meeting.extractRequired") });
      return;
    }
    setExtractedItems(smartExtract(transcript.trim(), language as Language));
    setNotice({ kind: "success", text: t("meeting.extracted") });
  };
  const updateExtracted = (id: string, patch: Partial<ActionItem>): void => {
    setExtractedItems((items) =>
      items.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    );
  };
  const toTodo = (
    action: ActionItem,
    sourceMeetingId: string | null,
  ): TodoItem => ({
    id: createId(),
    title: action.task,
    owner: action.owner,
    dueDate: action.dueDate ?? new Date().toISOString().slice(0, 10),
    time: "09:00",
    kind: "todo",
    completed: action.done,
    notes: title.trim() || t("meeting.new"),
    source: "smart-extract",
    sourceMeetingId,
    createdAt: new Date().toISOString(),
  });
  const addExtractedToTodos = async (): Promise<void> => {
    if (extractedItems.length === 0) return;
    await saveTodos(extractedItems.map((item) => toTodo(item, null)));
    await onTodosChanged();
    setNotice({ kind: "success", text: t("meeting.addedToTodos") });
  };
  const generate = async (): Promise<void> => {
    if (title.trim().length < 1 || transcript.trim().length < 20) {
      setNotice({ kind: "error", text: t("meeting.required") });
      return;
    }
    setBusy(true);
    setNotice(null);
    try {
      await new Promise((resolve) => setTimeout(resolve, 450));
      const summary = createMockMeetingSummary(
        transcript.trim(),
        language as Language,
        extractedItems,
      );
      const now = new Date().toISOString();
      const meeting: Meeting = {
        id: createId(),
        title: title.trim(),
        transcript: transcript.trim(),
        summary,
        sourceName,
        generatedWith: "local",
        summaryStatus: "pending",
        summaryAddedToTodo: false,
        createdAt: now,
        updatedAt: now,
      };
      await saveMeeting(meeting);
      await onRefresh();
      setSelectedId(meeting.id);
      setView("detail");
      setNotice({ kind: "success", text: t("meeting.created") });
    } catch (error) {
      setNotice({
        kind: "error",
        text:
          error instanceof Error && error.message === "INVALID_PROVIDER_URL"
            ? t("settings.invalidUrl")
            : t("meeting.failed"),
      });
    } finally {
      setBusy(false);
    }
  };
  const remove = async (meeting: Meeting): Promise<void> => {
    if (!confirm(t("meeting.deleteConfirm"))) return;
    await removeMeeting(meeting.id);
    await onRefresh();
    setView("list");
    setSelectedId(null);
  };
  const updateSummaryStatus = async (
    meeting: Meeting,
    status: "accepted" | "ignored",
  ): Promise<void> => {
    await saveMeeting({
      ...meeting,
      summaryStatus: status,
      updatedAt: new Date().toISOString(),
    });
    await onRefresh();
  };
  const saveSummaryEdit = async (meeting: Meeting): Promise<void> => {
    if (!summaryDraft.trim()) return;
    await saveMeeting({
      ...meeting,
      summary: { ...meeting.summary, executiveSummary: summaryDraft.trim() },
      summaryStatus: "accepted",
      updatedAt: new Date().toISOString(),
    });
    setEditingSummary(false);
    await onRefresh();
  };
  const addSummaryToTodo = async (meeting: Meeting): Promise<void> => {
    await saveTodo({
      id: createId(),
      title: `${t("meeting.reviewSummary")}: ${meeting.title}`,
      owner: "",
      dueDate: new Date().toISOString().slice(0, 10),
      time: "09:00",
      kind: "todo",
      completed: false,
      notes: meeting.summary.executiveSummary,
      source: "summary",
      sourceMeetingId: meeting.id,
      createdAt: new Date().toISOString(),
    });
    await saveMeeting({
      ...meeting,
      summaryAddedToTodo: true,
      updatedAt: new Date().toISOString(),
    });
    await Promise.all([onRefresh(), onTodosChanged()]);
    setNotice({ kind: "success", text: t("meeting.summaryAdded") });
  };

  if (view === "new")
    return (
      <section className="editor-grid">
        <article className="panel editor-panel">
          <div className="panel-heading">
            <div>
              <small>{t("meeting.new")}</small>
              <h2>{t("meeting.transcript")}</h2>
            </div>
            <button className="text-button" onClick={() => setView("list")}>
              {t("common.cancel")}
            </button>
          </div>
          <label className="field">
            <span>{t("meeting.title")}</span>
            <input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder={t("meeting.titlePlaceholder")}
            />
          </label>
          <label className="field">
            <span>{t("meeting.transcript")}</span>
            <textarea
              value={transcript}
              onChange={(event) => setTranscript(event.target.value)}
              placeholder={t("meeting.transcriptPlaceholder")}
              rows={16}
            />
          </label>
          <div className="smart-extract-bar">
            <div>
              <strong>✦ {t("meeting.smartExtract")}</strong>
              <small>{t("meeting.smartExtractHelp")}</small>
            </div>
            <button className="secondary" type="button" onClick={extract}>
              {t("meeting.extractButton")}
            </button>
          </div>
          {extractedItems.length > 0 && (
            <section className="extract-editor">
              <div className="extract-heading">
                <div>
                  <small>{t("meeting.mockEntities")}</small>
                  <h3>{t("meeting.extractedItems")}</h3>
                </div>
                <span className="count">{extractedItems.length}</span>
              </div>
              {extractedItems.map((item, index) => (
                <div className="extract-row" key={item.id}>
                  <span className="extract-index">{index + 1}</span>
                  <label>
                    <span>{t("meeting.action")}</span>
                    <input
                      value={item.task}
                      onChange={(event) =>
                        updateExtracted(item.id, { task: event.target.value })
                      }
                    />
                  </label>
                  <label>
                    <span>{t("meeting.owner")}</span>
                    <input
                      value={item.owner}
                      onChange={(event) =>
                        updateExtracted(item.id, { owner: event.target.value })
                      }
                    />
                  </label>
                  <label>
                    <span>{t("meeting.deadline")}</span>
                    <input
                      type="date"
                      value={item.dueDate ?? ""}
                      onChange={(event) =>
                        updateExtracted(item.id, {
                          dueDate: event.target.value || null,
                        })
                      }
                    />
                  </label>
                  <button
                    className="text-button danger-text"
                    type="button"
                    onClick={() =>
                      setExtractedItems((items) =>
                        items.filter((current) => current.id !== item.id),
                      )
                    }
                    aria-label={t("common.delete")}
                  >
                    ×
                  </button>
                </div>
              ))}
              <div className="extract-actions">
                <button
                  className="text-button"
                  type="button"
                  onClick={() =>
                    setExtractedItems((items) => [
                      ...items,
                      {
                        id: createId(),
                        task: "",
                        owner: "",
                        dueDate: null,
                        done: false,
                      },
                    ])
                  }
                >
                  + {t("meeting.addItem")}
                </button>
                <button
                  className="secondary"
                  type="button"
                  onClick={() => void addExtractedToTodos()}
                >
                  + {t("meeting.addToTodoList")}
                </button>
              </div>
            </section>
          )}
          <input
            ref={fileRef}
            hidden
            type="file"
            accept=".txt,.md,.srt,.vtt,text/plain,text/markdown"
            onChange={(event) => void handleFile(event.target.files?.[0])}
          />
          {sourceName && (
            <p className="file-note">
              {t("meeting.source")}: {sourceName}
            </p>
          )}
          {notice && <p className={`notice ${notice.kind}`}>{notice.text}</p>}
          <div className="form-actions">
            <button
              className="secondary"
              type="button"
              onClick={() => fileRef.current?.click()}
            >
              {t("meeting.import")}
            </button>
            <button
              className="primary"
              type="button"
              disabled={busy}
              onClick={() => void generate()}
            >
              {busy ? t("meeting.generating") : t("meeting.generate")}
            </button>
          </div>
          <p className="helper">{t("meeting.mockHint")}</p>
        </article>
        <aside className="panel preview-card">
          <div className="empty-icon">✦</div>
          <h2>{t("meeting.summary")}</h2>
          <p>{t("meeting.review")}</p>
        </aside>
      </section>
    );

  if (view === "detail" && selected)
    return (
      <section className="panel minutes-detail">
        <div className="detail-toolbar">
          <button className="text-button" onClick={() => setView("list")}>
            ← {t("meeting.back")}
          </button>
          <div>
            <button
              className="secondary"
              onClick={() =>
                download(
                  `${selected.title}.md`,
                  toMarkdown(selected),
                  "text/markdown",
                )
              }
            >
              {t("meeting.export")}
            </button>
            <button
              className="danger-button"
              onClick={() => void remove(selected)}
            >
              {t("meeting.delete")}
            </button>
          </div>
        </div>
        {notice && <p className={`notice ${notice.kind}`}>{notice.text}</p>}
        <small className="generated-label">
          {selected.generatedWith === "ai"
            ? t("meeting.aiGenerated")
            : t("meeting.localGenerated")}
        </small>
        <h2>{selected.title}</h2>
        <section className="summary-review">
          <div className="summary-review-heading">
            <div>
              <h3>{t("meeting.summary")}</h3>
              <span
                className={`summary-status ${selected.summaryStatus ?? "pending"}`}
              >
                {t(`meeting.status.${selected.summaryStatus ?? "pending"}`)}
              </span>
            </div>
            <div className="summary-actions">
              <button
                className="text-button"
                type="button"
                onClick={() => void updateSummaryStatus(selected, "accepted")}
              >
                ✓ {t("meeting.accept")}
              </button>
              <button
                className="text-button"
                type="button"
                onClick={() => {
                  setSummaryDraft(selected.summary.executiveSummary);
                  setEditingSummary(true);
                }}
              >
                {t("meeting.edit")}
              </button>
              <button
                className="text-button danger-text"
                type="button"
                onClick={() => void updateSummaryStatus(selected, "ignored")}
              >
                {t("meeting.ignore")}
              </button>
            </div>
          </div>
          {editingSummary ? (
            <div className="summary-editor">
              <textarea
                value={summaryDraft}
                onChange={(event) => setSummaryDraft(event.target.value)}
                rows={5}
              />
              <div>
                <button
                  className="secondary"
                  type="button"
                  onClick={() => setEditingSummary(false)}
                >
                  {t("common.cancel")}
                </button>
                <button
                  className="primary"
                  type="button"
                  onClick={() => void saveSummaryEdit(selected)}
                >
                  {t("meeting.saveEdit")}
                </button>
              </div>
            </div>
          ) : (
            <p
              className={
                selected.summaryStatus === "ignored" ? "ignored-summary" : ""
              }
            >
              {selected.summary.executiveSummary}
            </p>
          )}
          <button
            className="secondary add-summary-todo"
            type="button"
            disabled={selected.summaryAddedToTodo}
            onClick={() => void addSummaryToTodo(selected)}
          >
            {selected.summaryAddedToTodo ? "✓ " : "+ "}
            {selected.summaryAddedToTodo
              ? t("meeting.addedToTodo")
              : t("meeting.addToTodo")}
          </button>
        </section>
        <section>
          <h3>{t("meeting.decisions")}</h3>
          {selected.summary.decisions.length ? (
            <ul>
              {selected.summary.decisions.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          ) : (
            <p className="muted">{t("meeting.noDecisions")}</p>
          )}
        </section>
        <section>
          <h3>{t("meeting.actions")}</h3>
          {selected.summary.actionItems.map((item) => (
            <div className="result-action" key={item.id}>
              <span>{item.done ? "✓" : "○"}</span>
              <div>
                <strong>{item.task}</strong>
                <small>
                  {item.owner || t("meeting.unassigned")}
                  {item.dueDate ? ` · ${item.dueDate}` : ""}
                </small>
              </div>
            </div>
          ))}
        </section>
        <section>
          <h3>{t("meeting.risks")}</h3>
          {selected.summary.risks.length ? (
            <ul>
              {selected.summary.risks.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          ) : (
            <p className="muted">{t("meeting.noRisks")}</p>
          )}
        </section>
        <p className="review-warning">⚠ {t("meeting.review")}</p>
      </section>
    );

  return (
    <section className="panel">
      <div className="panel-heading">
        <div>
          <small>{t("meeting.saved")}</small>
          <h2>{t("nav.meetings")}</h2>
        </div>
        <button className="primary" type="button" onClick={openNew}>
          + {t("meeting.new")}
        </button>
      </div>
      <label className="search-field">
        <span className="sr-only">{t("meeting.search")}</span>
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t("meeting.searchPlaceholder")}
        />
      </label>
      <div className="meeting-list standalone">
        {filtered.length === 0 ? (
          <div className="empty-inline">
            <p>{t("meeting.none")}</p>
          </div>
        ) : (
          filtered.map((meeting) => (
            <button
              className="meeting-row"
              type="button"
              key={meeting.id}
              onClick={() => openDetail(meeting.id)}
            >
              <time>
                {new Intl.DateTimeFormat(language, {
                  dateStyle: "medium",
                }).format(new Date(meeting.updatedAt))}
              </time>
              <div>
                <strong>{meeting.title}</strong>
                <span>{meeting.summary.executiveSummary}</span>
              </div>
              <span className="arrow">→</span>
            </button>
          ))
        )}
      </div>
    </section>
  );
}
